import React, { createContext, useState, useEffect, useContext } from 'react';
import { getProperties, getPropertiesByAgency, createProperty } from '../services/propertyService';
import { AuthContext } from './AuthContext';

export const PropertyContext = createContext();

export const PropertyProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { agencyId, token } = useContext(AuthContext);

  // Load properties when both agencyId and token are available
  useEffect(() => {
    if (agencyId && token) {
      loadPropertiesByAgency(agencyId);
    } else if (agencyId && !token) {
      // AgencyId is available but no token yet - wait for authentication
      setLoading(false);
      setError(null);
    } else {
      // No agencyId available
      setProperties([]);
      setLoading(false);
      setError(null);
    }
  }, [agencyId, token]);

  const loadProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProperties();
      setProperties(data);
    } catch (err) {
      setError('Failed to load properties');
      console.error('Error loading properties:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPropertiesByAgency = async (agencyId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPropertiesByAgency(agencyId);
      setProperties(data);
      console.log(`Loaded ${data.length} properties for agency ID: ${agencyId}`);
    } catch (err) {
      setError('Failed to load properties for agency');
      console.error('Error loading properties by agency:', err);
    } finally {
      setLoading(false);
    }
  };

  const addProperty = async (propertyData) => {
    try {
      const response = await createProperty(propertyData);
      if (response.success) {
        // Add the new property to the local state
        setProperties(prev => [...prev, response.property]);
        return { success: true, property: response.property };
      } else {
        return { success: false, error: response.error };
      }
    } catch (err) {
      console.error('Error creating property:', err);
      return { success: false, error: 'Failed to create property' };
    }
  };

  const value = {
    properties,
    loading,
    error,
    loadProperties,
    loadPropertiesByAgency,
    addProperty,
    agencyId
  };

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
};
